export const HOME_THEME_STORAGE_KEY = "kavia-home-theme";

export const HOME_CONTENT = {
  es: {
    locale: "es-CO",
    seo: {
      title: "KaviaStore | Bienestar integral. Evolución consciente.",
      description: "Información clara sobre hábitos, bienestar e innovación para comprender mejor cómo cuidarnos cada día.",
    },
    controls: {
      language: "Seleccionar idioma",
      switchToLight: "Activar modo claro",
      switchToDark: "Activar modo oscuro",
    },
    accessibility: {
      skip: "Saltar al contenido",
      home: "KaviaStore, inicio",
      primaryNavigation: "Navegación principal",
      mobileNavigation: "Navegación móvil",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
      principles: "Principios de KaviaStore",
      wellbeingData: "Datos para comprender el bienestar",
      interpretation: "Cómo interpretamos la información",
      blogList: "Artículos publicados por KaviaStore",
      footerNavigation: "Enlaces del pie de página",
    },
    nav: [
      { id: "home", href: "#inicio", label: "Inicio" },
      { id: "wellbeing", href: "#bienestar", label: "Bienestar" },
      { id: "evidence", href: "#evidencia", label: "Evidencia" },
      { id: "blogs", href: "#blogs", label: "Blogs" },
      { id: "innovation", href: "#innovacion", label: "Innovación" },
      { id: "about", href: "#nosotros", label: "Nosotros" },
    ],
    dataCta: "Ver los datos",
    mobileDataCta: "Explorar los datos",
    hero: {
      eyebrow: "Una nueva mirada al bienestar",
      title: ["Tu bienestar,", "en constante", "evolución."],
      description: "Un espacio para comprender cómo los hábitos, el entorno, la prevención y los avances en salud pueden acompañar una vida más consciente.",
      primaryCta: "Explorar el bienestar",
      secondaryCta: "Conocer KaviaStore",
      pillars: [
        { id: "daily-care", title: "Cuidado cotidiano", text: "Decisiones pequeñas que encuentran lugar en tu rutina." },
        { id: "informed-choices", title: "Elecciones informadas", text: "Datos comprensibles, con su contexto y su fuente." },
        { id: "holistic-view", title: "Visión integral", text: "Hábitos, entorno y conocimiento conectados." },
      ],
      insight: {
        index: "PANORAMA GLOBAL · 2022",
        value: "31,3",
        text: "de los adultos no alcanzó la actividad física recomendada.",
        link: "Ver contexto",
      },
      vision: {
        index: "VISIÓN · 01",
        text: "Innovación al servicio de decisiones más humanas.",
      },
    },
    manifesto: {
      label: "Manifiesto",
      titleStart: "Creemos en un bienestar que conecta ",
      titleAction: "lo que haces",
      titleMiddle: ", lo que eliges y ",
      titleCare: "cómo te cuidas.",
      text: "Acercamos información y conocimiento a tu vida cotidiana para que construyas tu propia forma de bienestar.",
    },
    wellbeing: {
      label: "Bienestar",
      eyebrow: "A tu propio ritmo",
      title: "El bienestar se construye en lo cotidiano.",
      dailyIndex: "CUERPO · RITMO · ENTORNO",
      dailyText: "Creemos que el bienestar se construye en la forma en que vivimos cada día: cómo descansamos, nos movemos, cuidamos nuestro cuerpo y nos relacionamos con nuestro entorno.",
      processIndex: "TU PROCESO",
      processTitle: "Cada persona tiene su propio ritmo.",
      processText: "Por eso, evolucionar comienza con comprender lo que necesitas y encontrar maneras de cuidarte que tengan sentido para ti.",
      appliedKnowledge: "CONOCIMIENTO APLICADO",
      futureTitle: "El futuro del bienestar comienza en tu día a día.",
      visionIndex: "NUESTRA VISIÓN · EN EVOLUCIÓN",
      visionTitle: "Un espacio para descubrir, aprender y elegir.",
      visionText: "Nuestra aspiración es construir un espacio donde convivan conocimiento, métodos y servicios online que conecten la innovación con las necesidades humanas. Un lugar para descubrir, aprender y elegir con mayor conciencia.",
      visionNote: "Esta es nuestra dirección futura; no presentamos servicios futuros como si ya estuvieran disponibles.",
    },
    evidence: {
      label: "Evidencia",
      eyebrow: "Cifras con contexto",
      title: "Los números orientan. No definen una vida.",
      intro: "Estas cifras describen poblaciones y estudios concretos. No son una predicción individual ni una promesa de resultado.",
      stats: [
        {
          id: "physical-inactivity",
          value: "31,3%",
          label: "de los adultos",
          title: "no alcanzó la actividad física recomendada",
          text: "La estimación global corresponde a 2022: cerca de 1.800 millones de personas en 163 países y territorios.",
          source: "OMS / The Lancet · Datos 2022",
          href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11254784/",
          tone: "mint",
        },
        {
          id: "healthy-life-expectancy",
          value: "+11,4 / +10,0",
          label: "años estimados",
          title: "de diferencia en esperanza de vida desde los 50 años",
          text: "Mujeres y hombres que no fumaban, cuidaban su peso, se movían al menos 30 minutos al día y seguían una dieta de alta calidad, frente a quienes no cumplían ninguno de esos factores.",
          source: "Circulation · Estudio 2018",
          href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6207481/",
          tone: "cyan",
        },
        {
          id: "ncd-burden",
          value: "75%",
          label: "de las muertes globales",
          title: "no relacionadas con pandemias fueron por enfermedades no transmisibles",
          text: "La OMS estima al menos 43 millones de muertes en 2021 y señala factores conductuales, ambientales y metabólicos.",
          source: "OMS · Datos 2021",
          href: "https://www.who.int/es/news-room/fact-sheets/detail/noncommunicable-diseases",
          tone: "violet",
        },
        {
          id: "hypertension-control",
          value: "23%",
          label: "con hipertensión controlada",
          title: "entre adultos de 30 a 79 años que viven con esta condición",
          text: "La estimación mundial de 2024 muestra por qué la detección, el seguimiento y el acceso importan.",
          source: "OMS · Datos 2024",
          href: "https://www.who.int/es/news-room/fact-sheets/detail/hypertension",
          tone: "blue",
        },
      ],
      note: "El estudio de esperanza de vida fue observacional, se realizó con cohortes de profesionales de salud en Estados Unidos y encontró asociaciones; no demuestra que una conducta aislada cause el mismo resultado en cada persona.",
    },
    innovation: {
      eyebrow: "Ciencia, acceso y criterio",
      title: "La medicina avanzada amplía posibilidades. El cuidado sigue siendo humano.",
      textOne: "La OMS señala que los resultados diagnósticos influyen aproximadamente en el 70% de las decisiones sanitarias. También identifica aplicaciones de la genómica en evaluación de riesgo, diagnóstico, selección de tratamiento, seguimiento y desarrollo de medicamentos.",
      textTwo: "Eso no equivale a una garantía de longevidad. El beneficio depende de la condición, la evidencia, el acceso, el contexto clínico y el acompañamiento de profesionales cualificados.",
      diagnostics: "Diagnósticos · OMS",
      genomics: "Genómica · OMS",
    },
    about: {
      label: "Nosotros",
      eyebrow: "Bienestar integral. Evolución consciente.",
      title: "Una mirada que evoluciona contigo, sin dejar de ser clara.",
      cards: [
        { id: "origin", title: "Por qué nacimos", text: "KaviaStore nace con esa visión: acercar el bienestar integral a la vida cotidiana a través de información clara y nuevas posibilidades de cuidado personal." },
        { id: "integrity", title: "Cómo actuamos", text: "Entendemos la integridad como una forma de actuar: comunicar con honestidad, explicar lo que compartimos y respetar las decisiones de cada persona. Queremos que conozcas qué estás leyendo, de dónde viene y qué alcance tiene." },
        { id: "future", title: "Hacia dónde vamos", text: "Nos inspira el futuro del bienestar: más accesible, más comprensible y más conectado con la vida real." },
      ],
    },
    blogs: {
      label: "Blogs",
      eyebrow: "Biblioteca KaviaStore",
      title: "Ideas para entender tu cuerpo con más claridad.",
      intro: "Todos nuestros artículos publicados, reunidos en un solo lugar y escritos con fuentes visibles, contexto y límites claros.",
      readArticle: "Leer artículo",
    },
    trust: [
      { id: "sources", title: "Fuentes visibles", text: "Cada cifra enlaza a su publicación original." },
      { id: "context", title: "Contexto antes que impacto", text: "Indicamos población, año y límites relevantes." },
      { id: "education", title: "Información, no diagnóstico", text: "El contenido no reemplaza atención profesional." },
    ],
    closing: {
      eyebrow: "Porque cuidar de ti es un proceso que cambia contigo",
      title: "Tu siguiente paso es comprenderte mejor.",
      text: "KaviaStore. Tu bienestar, en constante evolución.",
      cta: "Explorar KaviaStore",
    },
    footer: {
      tagline: ["Bienestar integral.", "Evolución consciente."],
      medicalNote: "Contenido educativo y general. No sustituye la evaluación, el diagnóstico ni el tratamiento indicados por un profesional de la salud.",
    },
  },
  en: {
    locale: "en-US",
    seo: {
      title: "KaviaStore | Holistic wellbeing. Conscious evolution.",
      description: "Clear information about habits, wellbeing and innovation to better understand how we care for ourselves every day.",
    },
    controls: {
      language: "Select language",
      switchToLight: "Switch to light mode",
      switchToDark: "Switch to dark mode",
    },
    accessibility: {
      skip: "Skip to content",
      home: "KaviaStore, home",
      primaryNavigation: "Primary navigation",
      mobileNavigation: "Mobile navigation",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      principles: "KaviaStore principles",
      wellbeingData: "Data for understanding wellbeing",
      interpretation: "How we interpret information",
      blogList: "Articles published by KaviaStore",
      footerNavigation: "Footer links",
    },
    nav: [
      { id: "home", href: "#inicio", label: "Home" },
      { id: "wellbeing", href: "#bienestar", label: "Wellbeing" },
      { id: "evidence", href: "#evidencia", label: "Evidence" },
      { id: "blogs", href: "#blogs", label: "Blogs" },
      { id: "innovation", href: "#innovacion", label: "Innovation" },
      { id: "about", href: "#nosotros", label: "About us" },
    ],
    dataCta: "View the data",
    mobileDataCta: "Explore the data",
    hero: {
      eyebrow: "A new perspective on wellbeing",
      title: ["Your wellbeing,", "always", "evolving."],
      description: "A space to understand how habits, our environment, prevention and advances in health can support a more conscious life.",
      primaryCta: "Explore wellbeing",
      secondaryCta: "Discover KaviaStore",
      pillars: [
        { id: "daily-care", title: "Everyday care", text: "Small decisions that find a place in your routine." },
        { id: "informed-choices", title: "Informed choices", text: "Clear data, together with its context and source." },
        { id: "holistic-view", title: "Holistic perspective", text: "Habits, environment and knowledge, connected." },
      ],
      insight: {
        index: "GLOBAL OUTLOOK · 2022",
        value: "31.3",
        text: "of adults did not meet recommended physical activity levels.",
        link: "See context",
      },
      vision: {
        index: "VISION · 01",
        text: "Innovation in service of more human decisions.",
      },
    },
    manifesto: {
      label: "Manifesto",
      titleStart: "We believe in wellbeing that connects ",
      titleAction: "what you do",
      titleMiddle: ", what you choose and ",
      titleCare: "how you care for yourself.",
      text: "We bring information and knowledge into everyday life so you can build your own approach to wellbeing.",
    },
    wellbeing: {
      label: "Wellbeing",
      eyebrow: "At your own pace",
      title: "Wellbeing is built into everyday life.",
      dailyIndex: "BODY · RHYTHM · ENVIRONMENT",
      dailyText: "We believe wellbeing is built through the way we live each day: how we rest, move, care for our bodies and relate to our surroundings.",
      processIndex: "YOUR PROCESS",
      processTitle: "Everyone has their own pace.",
      processText: "That is why evolution begins with understanding what you need and finding ways to care for yourself that feel meaningful to you.",
      appliedKnowledge: "APPLIED KNOWLEDGE",
      futureTitle: "The future of wellbeing begins in your everyday life.",
      visionIndex: "OUR VISION · EVOLVING",
      visionTitle: "A space to discover, learn and choose.",
      visionText: "We aspire to build a space where knowledge, methods and online services connect innovation with human needs. A place to discover, learn and make more conscious choices.",
      visionNote: "This is our future direction; we do not present future services as though they were already available.",
    },
    evidence: {
      label: "Evidence",
      eyebrow: "Numbers in context",
      title: "Numbers can guide us. They do not define a life.",
      intro: "These figures describe specific populations and studies. They are not an individual prediction or a promise of results.",
      stats: [
        {
          id: "physical-inactivity",
          value: "31.3%",
          label: "of adults",
          title: "did not meet recommended physical activity levels",
          text: "The global estimate refers to 2022: nearly 1.8 billion people across 163 countries and territories.",
          source: "WHO / The Lancet · 2022 data",
          href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11254784/",
          tone: "mint",
        },
        {
          id: "healthy-life-expectancy",
          value: "+11.4 / +10.0",
          label: "estimated years",
          title: "of difference in life expectancy from age 50",
          text: "Women and men who did not smoke, maintained a healthy weight, moved for at least 30 minutes a day and followed a high-quality diet, compared with people who followed none of those factors.",
          source: "Circulation · 2018 study",
          href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6207481/",
          tone: "cyan",
        },
        {
          id: "ncd-burden",
          value: "75%",
          label: "of global deaths",
          title: "unrelated to pandemics were caused by noncommunicable diseases",
          text: "WHO estimates at least 43 million deaths in 2021 and highlights behavioural, environmental and metabolic risk factors.",
          source: "WHO · 2021 data",
          href: "https://www.who.int/news-room/fact-sheets/detail/noncommunicable-diseases",
          tone: "violet",
        },
        {
          id: "hypertension-control",
          value: "23%",
          label: "with controlled hypertension",
          title: "among adults aged 30 to 79 living with the condition",
          text: "The 2024 global estimate shows why detection, follow-up and access matter.",
          source: "WHO · 2024 data",
          href: "https://www.who.int/news-room/fact-sheets/detail/hypertension",
          tone: "blue",
        },
      ],
      note: "The life-expectancy research was observational, used cohorts of health professionals in the United States and found associations; it does not show that one behaviour alone causes the same outcome for every person.",
    },
    innovation: {
      eyebrow: "Science, access and sound judgement",
      title: "Advanced medicine expands possibilities. Care remains human.",
      textOne: "WHO notes that diagnostic results influence approximately 70% of healthcare decisions. It also identifies applications of genomics in risk assessment, diagnosis, treatment selection, monitoring and drug development.",
      textTwo: "That does not amount to a guarantee of longevity. Benefits depend on the condition, the evidence, access, clinical context and support from qualified professionals.",
      diagnostics: "Diagnostics · WHO",
      genomics: "Genomics · WHO",
    },
    about: {
      label: "About us",
      eyebrow: "Holistic wellbeing. Conscious evolution.",
      title: "A perspective that evolves with you, while always remaining clear.",
      cards: [
        { id: "origin", title: "Why we began", text: "KaviaStore was born from this vision: to bring holistic wellbeing into everyday life through clear information and new possibilities for personal care." },
        { id: "integrity", title: "How we act", text: "We see integrity as a way of acting: communicating honestly, explaining what we share and respecting every person's decisions. We want you to know what you are reading, where it comes from and what its limits are." },
        { id: "future", title: "Where we are going", text: "We are inspired by a future of wellbeing that is more accessible, easier to understand and more connected to real life." },
      ],
    },
    blogs: {
      label: "Blogs",
      eyebrow: "KaviaStore library",
      title: "Ideas to understand your body more clearly.",
      intro: "Every article we have published, gathered in one place and written with visible sources, context and clear limitations.",
      readArticle: "Read article",
    },
    trust: [
      { id: "sources", title: "Visible sources", text: "Every figure links to its original publication." },
      { id: "context", title: "Context before impact", text: "We identify the population, year and relevant limitations." },
      { id: "education", title: "Information, not diagnosis", text: "This content does not replace professional care." },
    ],
    closing: {
      eyebrow: "Because caring for yourself is a process that changes with you",
      title: "Your next step is to understand yourself better.",
      text: "KaviaStore. Your wellbeing, always evolving.",
      cta: "Explore KaviaStore",
    },
    footer: {
      tagline: ["Holistic wellbeing.", "Conscious evolution."],
      medicalNote: "Educational, general information. It does not replace assessment, diagnosis or treatment provided by a qualified healthcare professional.",
    },
  },
};
